import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Users, Target, Building, Zap, Phone, Mail, Linkedin, CheckCircle, BookOpen, FileText, Home, DollarSign, Sun, PieChart, TrendingUp, Search } from 'lucide-react';
import { getSaveaPhone, getSaveaPhoneTel } from '../config/phoneConfig';

export const AboutPage = () => {
  const { i18n } = useTranslation();

  const isSpanish = i18n.language === 'es';
  const country = isSpanish ? 'ES' : 'FR';
  const saveaPhone = getSaveaPhone(country, 'electricity');
  const saveaTel = getSaveaPhoneTel(country, 'electricity');

  const founderImageUrl = "/images/local/founder.png";
  
  // LinkedIn URL - placeholder for now, will be updated later
  const linkedinUrl = "https://www.linkedin.com/in/julien-mercier-01b737395";

  const tools = isSpanish ? [
    { icon: BookOpen, title: 'Guías y e-books anti-inflación', desc: 'El corazón de SAVEA en Francia, recursos prácticos para reducir tus gastos diarios.' },
    { icon: FileText, title: 'Generador de modelos de e-mails', desc: 'Cartas listas para enviar a tu proveedor de energía, banco, seguro u operador de Internet para renegociar contratos.' },
    { icon: Home, title: 'To-Do List mudanza', desc: 'Una herramienta inteligente para organizar todos tus trámites administrativos.' },
    { icon: Zap, title: 'Comparador de ofertas energía e Internet', desc: 'Encuentra las tarifas más ventajosas del momento.' },
    { icon: Sun, title: 'Simulador de ahorros solares', desc: 'Estima cuánto puedes ahorrar con paneles fotovoltaicos.' },
    { icon: PieChart, title: 'Seguimiento mensual de gastos', desc: 'Visualiza tus costes fijos y optimiza tus presupuestos.' },
    { icon: TrendingUp, title: 'Evolución de la inflación y precios', desc: 'Sigue las variaciones en productos esenciales.' },
    { icon: Search, title: 'Historical Price Tracker', desc: 'Accede al historial de precios para anticipar mejor tus compras.' }
  ] : [
    { icon: BookOpen, title: 'Guides et e-books anti-inflation', desc: 'Le cœur de SAVEA en France, des ressources pratiques pour réduire vos dépenses au quotidien.' },
    { icon: FileText, title: 'Générateur de modèles d\'e-mails', desc: 'Des lettres prêtes à envoyer à votre fournisseur d\'énergie, banque, assurance ou opérateur Internet pour renégocier vos contrats.' },
    { icon: Home, title: 'To-Do List déménagement', desc: 'Un outil intelligent pour organiser toutes vos démarches administratives.' },
    { icon: Zap, title: 'Comparateur d\'offres énergie et Internet', desc: 'Trouvez les tarifs les plus avantageux du moment.' },
    { icon: Sun, title: 'Simulateur d\'économies solaires', desc: 'Estimez combien vous pouvez économiser avec des panneaux photovoltaïques.' },
    { icon: PieChart, title: 'Suivi mensuel des dépenses', desc: 'Visualisez vos coûts fixes et optimisez vos budgets.' },
    { icon: TrendingUp, title: 'Évolution de l\'inflation et des prix', desc: 'Suivez les variations sur les produits essentiels.' },
    { icon: Search, title: 'Historical Price Tracker', desc: 'Accédez à l\'historique des prix pour mieux anticiper vos achats.' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-sage-100 text-sage-800">
              {isSpanish ? 'Nuestra Historia' : 'Notre Histoire'}
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-sage-900 mb-6">
              {isSpanish ? 'Quiénes Somos' : 'Qui Sommes-Nous'}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto leading-relaxed">
              {isSpanish
                ? 'SAVEA es una plataforma creada para ayudar a cada hogar a recuperar el control de sus gastos y luchar contra la inflación gracias a herramientas simples, gratuitas y accesibles para todos.'
                : 'SAVEA est une plateforme créée pour aider chaque foyer à reprendre le contrôle de ses dépenses et lutter contre l\'inflation grâce à des outils simples, gratuits et accessibles à tous.'
              }
            </p>
          </div>

          {/* Founder Section */}
          <Card className="mb-12">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                {/* Photo - Round and smaller */}
                <div className="flex-shrink-0">
                  <img 
                    src={founderImageUrl}
                    alt="Julien Mercier - Fondateur SAVEA"
                    className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-sage-200 shadow-lg"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-sage-600" />
                  <div>
                    <h2 className="text-2xl font-bold text-sage-900">Julien Mercier</h2>
                    <p className="text-sage-600">
                      {isSpanish ? 'Fundador de SAVEA' : 'Fondateur de SAVEA'}
                    </p>
                    <a 
                      href={linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                      LinkedIn
                    </a>
                  </div>
                </div>
                
                <p className="text-sage-700 leading-relaxed mb-6">
                  {isSpanish
                    ? 'Emprendedor francés establecido en España, Julien Mercier fundó SAVEA después de escribir una guía anti-inflación para sus amigos. Apasionado de las finanzas y el trading, rápidamente comprendió que la mayoría de los hogares perdían poder adquisitivo no por falta de dinero, sino por falta de información y herramientas.'
                    : 'Entrepreneur français installé en Espagne, Julien Mercier a fondé SAVEA après avoir rédigé un guide anti-inflation pour ses amis. Passionné de finance et de trading, il a vite compris que la plupart des foyers perdaient du pouvoir d\'achat non pas par manque d\'argent, mais par manque d\'informations et d\'outils.'
                  }
                </p>

                <blockquote className="border-l-4 border-sage-600 pl-4 italic text-sage-700 mb-6">
                  {isSpanish
                    ? '"De esta constatación nació SAVEA, un sitio pensado para simplificar la gestión del hogar y hacer las economías accesibles a todos."'
                    : '"De ce constat est né SAVEA, un site pensé pour simplifier la gestion du foyer et rendre les économies accessibles à tous."'
                  }
                </blockquote>

                {/* LinkedIn Button */}
                <Button
                  variant="outline"
                  className="border-sage-300 hover:bg-sage-50"
                  onClick={() => window.open(linkedinUrl, '_blank')}
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Conectar en LinkedIn' : 'Connecter sur LinkedIn'}
                </Button>
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Mission */}
          <Card className="mb-12 bg-gradient-to-br from-sage-600 to-sage-700 text-white border-0">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-8 h-8" />
                <h3 className="text-2xl font-bold">
                  {isSpanish ? 'Nuestra Misión' : 'Notre Mission'}
                </h3>
              </div>
              <p className="text-xl leading-relaxed text-sage-50">
                {isSpanish
                  ? 'Dar a cada hogar los medios para consumir inteligentemente, sin renunciar a su comodidad ni a su libertad.'
                  : 'Donner à chaque foyer les moyens de consommer intelligemment, sans renoncer à son confort ni à sa liberté.'
                }
              </p>
            </CardContent>
          </Card>

          {/* Business Model */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-sage-600" />
                {isSpanish ? 'Modelo de Negocio Transparente' : 'Modèle Économique Transparent'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                {isSpanish
                  ? 'Para hacer el proyecto sostenible — ya que requería cada vez más tiempo y recursos — Julien eligió colaborar con ciertas marcas y socios, que pagan a SAVEA una comisión cuando sus servicios son recomendados, sin impactar el precio final.'
                  : 'Pour rendre le projet durable — car il demandait de plus en plus de temps et de ressources — Julien a choisi de collaborer avec certaines marques et partenaires, qui versent à SAVEA une commission lorsque leurs services sont recommandés, sans impacter le prix final.'
                }
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-semibold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  {isSpanish
                    ? 'Este enfoque permite mantener todas las herramientas gratuitas para los usuarios.'
                    : 'Cette approche permet de maintenir tous les outils gratuits pour les utilisateurs.'
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tools */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-sage-600" />
                {isSpanish ? 'Las herramientas disponibles en SAVEA' : 'Les outils disponibles sur SAVEA'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {tools.map((tool, index) => {
                  const Icon = tool.icon;
                  return (
                    <div key={index} className="flex gap-4 p-4 rounded-lg border border-sage-200 hover:border-sage-400 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-sage-100 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-sage-600" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sage-900 mb-1">{tool.title}</h4>
                        <p className="text-sm text-sage-600">{tool.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-6 h-6 text-sage-600" />
                {isSpanish ? 'Información Legal' : 'Informations Légales'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-sage-700">
              <p>
                <strong>{isSpanish ? 'Editado por' : 'Édité par'}:</strong> SOLUCIONES INTEGRALES HORIZON, S.L.U.
              </p>
              <p>
                <strong>NIF:</strong> B23972946
              </p>
              <p>
                <strong>{isSpanish ? 'Domicilio social' : 'Siège social'}:</strong> Calle Radas 57, Ático 1, 08004 Barcelona, España
              </p>
              <p>
                <strong>{isSpanish ? 'Representante Legal' : 'Représentant Légal'}:</strong> Julien Mercier (NIE: Y9783769C)
              </p>
              <p className="text-sage-600 text-xs mt-4">
                {isSpanish
                  ? 'Sociedad registrada en España, que funciona gracias a asociaciones transparentes con marcas seleccionadas, garantizando al mismo tiempo el acceso gratuito a todas sus herramientas.'
                  : 'Société enregistrée en Espagne, et fonctionne grâce à des partenariats transparents avec des marques sélectionnées, tout en garantissant l\'accès gratuit à tous ses outils.'
                }
              </p>
            </CardContent>
          </Card>

          {/* CTA Contact */}
          <Card className="bg-gradient-to-r from-sage-600 to-sage-700 border-0">
            <CardContent className="p-8 text-center">
              <Zap className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-4">
                {isSpanish ? '¿Tienes una pregunta?' : 'Vous avez une question ?'}
              </h3>
              <p className="text-sage-100 mb-6 max-w-2xl mx-auto">
                {isSpanish
                  ? 'Nuestros especialistas están disponibles para ayudarte a encontrar las mejores ofertas adaptadas a tu situación.'
                  : 'Nos spécialistes sont disponibles pour vous aider à trouver les meilleures offres adaptées à votre situation.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={`tel:${saveaTel}`}
                  className="inline-flex items-center justify-center bg-sage-700 text-white hover:bg-sage-800 font-bold text-lg px-8 py-4 rounded-full transition-all duration-300"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {isSpanish ? 'Llamar Ahora' : 'Appeler Maintenant'}: <span className="savea-phone-number">{saveaPhone}</span>
                </a>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => window.location.href = isSpanish ? '/es/contacto' : '/fr/contact'}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  {isSpanish ? 'Contactar' : 'Nous Contacter'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
